/** Extracts the "Role Details" paragraph text from a job HTML template. */
export const extractRoleDetails = (htmlTemplate: string): string | null => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlTemplate, 'text/html');
  const roleDetailsTitle = Array.from(doc.querySelectorAll('p > strong')).find(
    (el) => el.textContent?.trim() === 'Role Details',
  );

  if (!roleDetailsTitle) return null;

  const roleDetailsContent: string[] = [];
  let sibling = roleDetailsTitle.parentElement?.nextElementSibling;

  while (sibling && !sibling.querySelector('strong')) {
    roleDetailsContent.push(sibling.textContent ?? '');
    sibling = sibling.nextElementSibling;
  }

  return roleDetailsContent.join(' ').trim();
};

/** Formats a unix-seconds start date as "Jan 5". */
export const formatJobStartDate = (startDate: number): string =>
  new Date(startDate * 1000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

/** Formats a unix-seconds timestamp as YYYY-MM-DD, or TBD when invalid. */
export const formatUnixDate = (timestamp: number | string): string => {
  if (!timestamp) return 'TBD';

  const numericTimestamp = typeof timestamp === 'string' ? Number(timestamp) : timestamp;

  if (isNaN(numericTimestamp) || numericTimestamp === 0) {
    return 'TBD';
  }

  try {
    const date = new Date(numericTimestamp * 1000);
    if (isNaN(date.getTime())) {
      return 'TBD';
    }
    return date.toISOString().split('T')[0];
  } catch (error) {
    return 'TBD';
  }
};

/** Humanizes the time left until a unix-seconds end date. */
export const calculateDaysLeftText = (endDate?: number | string): string => {
  const endTimestamp = typeof endDate === 'string' ? Number(endDate) : endDate;

  if (!endTimestamp || endTimestamp <= 0 || isNaN(endTimestamp)) {
    return 'TBD';
  }

  try {
    const end = new Date(endTimestamp * 1000);
    const currentDate = new Date();

    if (isNaN(end.getTime())) {
      return 'TBD';
    }

    const timeDiff = end.getTime() - currentDate.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

    if (daysDiff < 0) {
      return 'Expired';
    } else if (daysDiff === 0) {
      return 'Expires today';
    } else if (daysDiff === 1) {
      return '1 day left';
    } else {
      return `${daysDiff} days left`;
    }
  } catch (error) {
    return 'TBD';
  }
};

/** Whole days remaining until a unix-seconds end date (0 when past/invalid). */
export const calculateDaysLeftCount = (endDate?: number | string): number => {
  const endTimestamp = typeof endDate === 'string' ? Number(endDate) : endDate;
  if (!endTimestamp || endTimestamp <= 0 || isNaN(endTimestamp)) return 0;
  return Math.max(
    0,
    Math.ceil((new Date(endTimestamp * 1000).getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
  );
};

/** Formats recorder seconds as MM:SS. */
export const formatRecordingTime = (secs: number): string => {
  const minutes = Math.floor(secs / 60).toString().padStart(2, '0');
  const seconds = (secs % 60).toString().padStart(2, '0');
  return `${minutes}:${seconds}`;
};

/** Display name for a content-language code. */
export const langName = (code: string, names: Record<string, string>): string =>
  names[code] ?? code.toUpperCase();

/** Counts the selected values across all filter groups. */
export const countActiveFilters = (filters: {
  jobTitle: string[];
  location: string[];
  workType: string[];
  department: string[];
}): number =>
  [...filters.jobTitle, ...filters.location, ...filters.workType, ...filters.department].length;
